// modules/auth/index.ts — Platik-IA
// Autenticación Google OAuth via Supabase.
// El provider_token de Google caduca a la hora y Supabase no lo refresca, así que
// guardamos el provider_refresh_token (cifrado, vía Edge Function google-token) y
// pedimos access_tokens frescos on-demand. Ver services/google-auth.ts.

import { bus }      from '../../core/event-bus';
import { supabase } from '../../services/supabase';
import { storeGoogleRefreshToken, fetchFreshAccessToken, GoogleReauthError } from '../../services/google-auth';
import type { AuthAPI }  from './contracts';
import type { User }     from '../../shared/types/global';
import type { Session as SupaSession } from '@supabase/supabase-js';

let _user:          User | null   = null;
let _providerToken: string | null = null;
let _tokenExpiresAt = 0;           // epoch ms; 0 = desconocido/forzar refresh
let _hasSession     = false;       // ¿hay sesión persistida? (decidido en init)

const TOKEN_MARGIN_MS = 60_000;    // refrescar 1 min antes de caducar

/** Borra cualquier parámetro de auth (code/token) de la URL y del historial. */
function stripAuthParamsFromUrl(): void {
  const { hash, search, pathname } = window.location;
  if (hash.includes('access_token') || hash.includes('refresh_token') ||
      new URLSearchParams(search).has('code')) {
    window.history.replaceState({}, document.title, pathname);
  }
}

function mapUser(session: SupaSession): User {
  const meta = session.user.user_metadata;
  return {
    id:           session.user.id,
    email:        session.user.email ?? '',
    name:         meta.full_name ?? meta.name ?? 'Usuario',
    avatarUrl:    meta.avatar_url ?? null,
    role:         'user',   // se sobreescribe al cargar perfil de Supabase
    plan:         'free',
    nickname:     null,
    sheetId:      null,
    hasGeminiKey: false,
  };
}

/** Si la sesión trae un provider_refresh_token (sólo tras el consent de Google),
 *  lo persiste cifrado en el servidor para poder refrescar el access token luego. */
function captureRefreshToken(session: SupaSession): void {
  const refreshToken = session.provider_refresh_token;
  if (refreshToken) {
    storeGoogleRefreshToken(refreshToken).catch(err =>
      console.warn('[auth] No se pudo guardar el refresh token de Google:', err));
  }
}

/** Carga el perfil extendido desde nuestra tabla y emite signed-in.
 *  Se ejecuta FUERA del callback de onAuthStateChange (deferido) para no
 *  provocar el deadlock del lock interno de supabase-js. */
async function loadProfileAndEmit(session: SupaSession): Promise<void> {
  _user          = mapUser(session);
  _providerToken = session.provider_token ?? _providerToken;

  // Columnas explícitas: NO pedir google_refresh_token_enc (sin permiso de SELECT
  // para `authenticated` por hardening de columnas; lo maneja solo la Edge Function).
  const { data, error } = await supabase
    .from('users')
    .select('role, plan, nickname, sheet_id, gemini_key_enc')
    .eq('id', session.user.id)
    .single();

  if (error) console.error('[auth] No se pudo cargar el perfil:', error.message);

  if (data && _user) {
    _user.role         = (data.role  ?? 'user')  as 'user' | 'admin';
    _user.plan         = (data.plan  ?? 'free')  as 'free' | 'pro';
    _user.nickname     = data.nickname    ?? null;
    _user.sheetId      = data.sheet_id    ?? null;
    _user.hasGeminiKey = !!data.gemini_key_enc;
  }

  stripAuthParamsFromUrl();
  bus.emit('auth:signed-in', { user: _user, providerToken: _providerToken ?? '' });
}

export async function initAuth(): Promise<AuthAPI> {
  // Escuchar cambios de sesión. El callback NO debe ser async ni await llamadas
  // de supabase dentro (deadlock); deferimos la carga del perfil con setTimeout.
  // INITIAL_SESSION lo maneja getSession() abajo para evitar doble emisión.
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT' || !session) {
      _user          = null;
      _providerToken = null;
      _tokenExpiresAt = 0;
      _hasSession    = false;
      if (event === 'SIGNED_OUT') bus.emit('auth:signed-out');
      return;
    }
    if (event === 'SIGNED_IN') {
      // Login fresco: el provider_token es válido (~1h) y puede traer refresh token.
      _providerToken  = session.provider_token ?? _providerToken;
      _tokenExpiresAt = Date.now() + 50 * 60_000;
      captureRefreshToken(session);
      setTimeout(() => { void loadProfileAndEmit(session); }, 0);
    }
  });

  // Determinar la sesión persistida ANTES de devolver el API, para que main.ts
  // sepa si mostrar el spinner de carga en vez de la pantalla de login.
  const { data: { session } } = await supabase.auth.getSession();
  _hasSession = !!session;
  if (session) {
    // Sesión reabierta: el provider_token guardado probablemente esté caducado,
    // así que dejamos _tokenExpiresAt = 0 → getAccessToken() lo refrescará.
    _providerToken = session.provider_token ?? null;
    captureRefreshToken(session);
    setTimeout(() => { void loadProfileAndEmit(session); }, 0);
  }

  bus.emit('auth:ready');

  /** Devuelve un access_token de Google válido, refrescándolo si hace falta. */
  async function getAccessToken(): Promise<string | null> {
    const now = Date.now();
    if (_providerToken && now < _tokenExpiresAt - TOKEN_MARGIN_MS) {
      return _providerToken;
    }
    try {
      const { accessToken, expiresIn } = await fetchFreshAccessToken();
      _providerToken  = accessToken;
      _tokenExpiresAt = now + expiresIn * 1000;
      return accessToken;
    } catch (err) {
      if (err instanceof GoogleReauthError) {
        bus.emit('auth:reauth-needed');
        return null;
      }
      console.warn('[auth] No se pudo refrescar el access token:', err);
      return _providerToken;   // último recurso: usar el que haya (puede fallar)
    }
  }

  return {
    getUser:        () => _user,
    hasSession:     () => _hasSession,
    getAccessToken,
    setSheetId:     (id: string) => { if (_user) _user.sheetId = id; },

    async signIn() {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          scopes:       'https://www.googleapis.com/auth/spreadsheets',
          redirectTo:   window.location.origin,
          queryParams:  { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) bus.emit('auth:error', { code: error.name, message: error.message });
    },

    async signOut() {
      await supabase.auth.signOut();
    },

    dispose() {
      subscription.unsubscribe();
    },
  };
}
